import { T, tt } from '@agos/shared'
import { useEffect, useState } from 'react'

/**
 * The offer to install the site as an app.
 *
 * **The browser decides whether this is possible, not the page.** Chrome fires `beforeinstallprompt`
 * only when its own criteria are met — a manifest, a service worker, HTTPS, and not already installed —
 * and the event must be kept so `prompt()` can be called from a real user gesture later. Safari fires
 * nothing at all and has no API, so on iOS there is no button to show and none is shown; inventing an
 * "install me" card there would be advertising something the browser will not do.
 *
 * **Dismissal is remembered.** A prompt that returns on every visit is the thing people install an app
 * to get away from. It is kept in `localStorage` rather than session, because *no* means no for longer
 * than one tab.
 */

const DISMISSED_KEY = 'agos-install-dismissed'

interface InstallEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function alreadyDismissed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

export function InstallPwaPrompt() {
  const [offer, setOffer] = useState<InstallEvent | null>(null)
  const [dismissed, setDismissed] = useState(alreadyDismissed)

  useEffect(() => {
    function capture(event: Event) {
      // Without this the browser shows its own bar wherever it likes; keeping the event is what lets
      // the offer sit somewhere the customer is looking.
      event.preventDefault()
      setOffer(event as InstallEvent)
    }
    window.addEventListener('beforeinstallprompt', capture)
    return () => window.removeEventListener('beforeinstallprompt', capture)
  }, [])

  if (offer === null || dismissed) return null

  return (
    <div className="rounded-2xl border border-border bg-card p-3 space-y-2" data-install-prompt>
      <p className="text-xs">
        <T>Pridajte si nás na plochu - objednávka je potom na dva klepnutia.</T>
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          data-install-accept
          onClick={() => {
            // Must be inside the gesture. Awaiting anything first loses the right to prompt.
            void offer.prompt().then(() => setOffer(null))
          }}
          className="min-h-[36px] flex-1 rounded-xl bg-primary text-primary-foreground text-xs font-bold"
        >
          <T>Pridať</T>
        </button>
        <button
          type="button"
          data-install-dismiss
          onClick={() => {
            try {
              localStorage.setItem(DISMISSED_KEY, '1')
            } catch {
              // The offer simply comes back next time; nothing else depends on this.
            }
            setDismissed(true)
          }}
          className="min-h-[36px] flex-1 rounded-xl border border-border text-xs font-semibold"
        >
          {tt('Nie, ďakujem')}
        </button>
      </div>
    </div>
  )
}
